import type { Location, UpdateLocation } from '@quromedical/models';
import type { OrganizationFetcher } from 'core/group';
import {
  Alert,
  Skeleton,
  SkeletonProvider,
  Snackbar,
  Visible,
} from 'design-system';
import { logger } from 'helpers';
import { useFiniteState } from 'hooks/useFiniteState';
import { LocationApi } from 'integration/aggregate';
import { useCallback } from 'react';
import { strings } from 'strings';

import { GeneralCard } from './card';
import { GeneralForm } from './form';

interface LocationFormCardProps {
  canEdit: boolean;
  data: Location;
  id: string;
  revalidate: () => void;
  organizationFetcher: OrganizationFetcher;
}

type FormState = 'initial' | 'editing' | 'submitting' | 'error';
const api = new LocationApi();

export const LocationFormCard: React.FC<LocationFormCardProps> = ({
  canEdit,
  data,
  id,
  revalidate,
  organizationFetcher,
}) => {
  const state = useFiniteState<FormState>('initial');

  const onSubmit = useCallback(
    async (formData: Omit<UpdateLocation, 'id'>) => {
      state.set('submitting');
      try {
        await api.updateLocation({ ...formData, id });
        state.set('initial');
        revalidate();
        return {};
      } catch (error) {
        state.set('error');
        logger.error(error);
        return { error };
      }
    },
    [id, revalidate, state],
  );

  return (
    <>
      <SkeletonProvider loading={state.is('submitting')}>
        <Skeleton>
          <Visible if={state.isNot('editing')}>
            <GeneralCard
              data={data}
              canEdit={canEdit}
              onEditPress={state.next('editing')}
            />
          </Visible>

          <Visible if={state.is('editing')}>
            <GeneralForm
              data={data}
              onSubmit={onSubmit}
              onCancel={state.next('initial')}
              organizationFetcher={organizationFetcher}
              initialOrganization={data.managingOrganization}
            />
          </Visible>
        </Skeleton>
      </SkeletonProvider>

      <Snackbar onClose={state.next('editing')} isOpen={state.is('error')}>
        <Alert
          backgroundColor="status-critical"
          textColor="white"
          onAction={state.next('editing')}
          actionIcon="close"
          title={strings.ErrorCardTitle}
          body={strings.GenericErrorMessage}
        />
      </Snackbar>
    </>
  );
};
